/**
 * `Freeze columns` — a checklist of the table's columns (`lib/table-freeze.ts`, client request
 * 14 Oct 2026). The ticked ones move to the left edge and stay put while the rest scroll
 * sideways; untick to put a column back. At most `max` (3): past that the frozen block crowds out
 * the columns it is meant to keep in view, and the select disables the remaining options rather
 * than letting a fourth tick silently bump an earlier one. Shows the ticked column names as one
 * line of text (`Name, Nat, Status`), so the reader sees what is frozen without opening it — antd's
 * own tags collapsed to `+ 3 …` in a control this narrow; "None" when nothing is.
 */
import { Select } from 'antd';
import { FREEZE_MAX, type FreezableColumn } from '@/lib/table-freeze';
import { FullViewButton, type FullViewState } from './FullView';
/* Loaded with the control because every list that freezes antd columns renders one. */
import './freeze-columns.css';

export interface FreezeColumnsControlProps {
  /** The columns the user may tick, in the table's usual order. */
  columns: readonly FreezableColumn[];
  /** The ticked keys (`useFrozenColumns`). */
  value: readonly string[];
  onChange: (keys: string[]) => void;
  max?: number;
  /** `useFullView()` — when given, the `Full view` button sits beside the select. */
  fullView?: FullViewState;
}

export function FreezeColumnsControl({ columns, value, onChange, max = FREEZE_MAX, fullView }: FreezeColumnsControlProps) {
  const names = columns.filter((c) => value.includes(c.key)).map((c) => c.title).join(', ');
  const select = (
    <Select<string[]>
      mode="multiple"
      size="small"
      aria-label="Freeze columns"
      title={`Tick up to ${max} column${max === 1 ? '' : 's'} to keep at the left while the table scrolls sideways. Remembered on this computer.`}
      prefix={<span style={{ color: 'var(--text-muted)' }}>Freeze columns</span>}
      placeholder="None"
      allowClear
      maxCount={max}
      maxTagCount={0}
      maxTagPlaceholder={() => <span title={names}>{names}</span>}
      popupMatchSelectWidth={false}
      showSearch={false}
      value={[...value]}
      options={columns.map((c) => ({ value: c.key, label: c.title }))}
      onChange={onChange}
      /* Wide enough for the names it shows: the prefix and arrow take ~175px, a name ~7.5px a
       * letter. antd truncates a tag that does not fit, which hid the very thing it is for. */
      style={{ width: Math.min(440, Math.max(230, 175 + names.length * 7.5)) }}
    />
  );
  if (!fullView) return select;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {select}
      <FullViewButton view={fullView} />
    </span>
  );
}
